/**
 * Mock API simulator.
 *
 * Turns the in-app mock layer into a near-production API simulator
 * so frontend behaviour matches what a real network will deliver
 * before any backend exists. Models the four things that catch
 * UIs off-guard once they hit a real API:
 *
 *   1. Latency      — configurable preset (fast / normal / slow / random)
 *   2. Errors       — random injection at a configurable rate, weighted
 *                     across the common HTTP failure modes
 *   3. Abort        — every awaited delay respects an AbortSignal so
 *                     stores can cancel in-flight requests when the
 *                     user navigates away
 *   4. Deduplication — identical concurrent reads share one inflight
 *                     promise; reduces redundant work and matches
 *                     real-world HTTP-cache semantics
 *
 * Future backend cutover:
 *   When `USE_MOCKS=false`, services bypass this module entirely and
 *   call `http.get/post/...` from `src/api/client.js`. Nothing in
 *   this file ships to production.
 *
 * Runtime tweaking:
 *   In a dev console:
 *     window.__mock.setTiming('slow')
 *     window.__mock.setErrorRate(0.25)
 *     window.__mock.forceNext('404')
 *
 *   The latter is helpful for one-shot QA of error / not-found
 *   states without permanently raising the error rate.
 */

import { sleep } from '@/utils/async'
import config from './config'
import { HttpError, AuthError, NetworkError, TimeoutError } from './errors'

/* ════════════════════════════════════════════════════════════════
 * Configuration
 * ════════════════════════════════════════════════════════════════ */

const TIMING_PRESETS = Object.freeze({
  /** Tight, near-zero latency. Useful for tests + quick scrolling. */
  fast: { min: 30, max: 80 },
  /** Default. Mirrors a healthy production API on a good network. */
  normal: { min: 120, max: 320 },
  /** A slow regional network or a cold cache. Surfaces loading UI. */
  slow: { min: 800, max: 2000 },
  /** Uniformly random across the whole range. Forces edge-case UX. */
  random: { min: 50, max: 2500 },
})

export type SimulatedErrorKey =
  | '401'
  | '403'
  | '404'
  | '429'
  | '500'
  | '502'
  | '503'
  | 'timeout'
  | 'network'

const ERROR_FACTORIES: Record<SimulatedErrorKey, () => Error> = {
  401: () => new AuthError(401, 'Authentication required (simulated)'),
  403: () => new AuthError(403, 'Forbidden (simulated)'),
  404: () => new HttpError(404, 'Not found (simulated)'),
  429: () => new HttpError(429, 'Rate limited (simulated)'),
  500: () => new HttpError(500, 'Internal server error (simulated)'),
  502: () => new HttpError(502, 'Bad gateway (simulated)'),
  503: () => new HttpError(503, 'Service unavailable (simulated)'),
  timeout: () => new TimeoutError('Request timed out (simulated)'),
  network: () => new NetworkError('Network unreachable (simulated)'),
}

/* Weighted random — server errors more common than auth errors in
 * the wild. Tuned for a realistic feel during demos. */
const ERROR_WEIGHTS: Array<[SimulatedErrorKey, number]> = [
  ['500', 0.3],
  ['503', 0.15],
  ['429', 0.1],
  ['timeout', 0.15],
  ['network', 0.2],
  ['401', 0.05],
  ['404', 0.05],
]

/* Mutable state — overridable at runtime via window.__mock. */
type TimingPreset = keyof typeof TIMING_PRESETS

const state: {
  timing: TimingPreset
  errorRate: number
  pinnedError: SimulatedErrorKey | null
  errorAllowlist: Set<string>
} = {
  timing: (config.mockTiming as TimingPreset) || 'normal',
  errorRate: typeof config.mockErrorRate === 'number' ? config.mockErrorRate : 0,
  /** Pinned error for the next request. Set via forceNext(). */
  pinnedError: null,
  /** Endpoints exempt from random error injection. */
  errorAllowlist: new Set(),
}

/* ════════════════════════════════════════════════════════════════
 * Public API — used by services
 * ════════════════════════════════════════════════════════════════ */

/**
 * The canonical mock-request wrapper. Replaces direct `sleep()`
 * calls; awaits configurable latency + injects errors + respects
 * AbortSignal.
 *
 *   await mockRequest(value, {
 *     signal,                       // optional AbortController.signal
 *     delayMs,                      // override default preset latency
 *     path:   '/trademarks',        // for logging + allowlists
 *     method: 'GET',                // for logging
 *     noError: false                // skip random error injection
 *   })
 *
 * Returns a deep-cloned copy of `value` so callers can mutate the
 * result without polluting the underlying mock fixture.
 */
export interface MockRequestOptions {
  signal?: AbortSignal | null
  delayMs?: number
  path?: string
  method?: string
  noError?: boolean
}

export async function mockRequest<T>(value: T, opts: MockRequestOptions = {}): Promise<T> {
  const { signal, delayMs, path = '', method = 'GET', noError = false } = opts

  /* Fast-path for already-aborted signals — don't even sleep. */
  if (signal?.aborted) throw createAbortError(signal.reason)

  await abortableSleep(delayMs ?? randomLatency(), signal)

  /* Pinned error (one-shot) trumps the random roll. */
  if (state.pinnedError) {
    const errKey = state.pinnedError
    state.pinnedError = null
    throw makeError(errKey)
  }

  if (!noError) maybeFailRandomly(`${method} ${path}`)

  /* `null` is a legitimate "not found" response — keep it null. */
  if (value == null) return value

  /* Deep clone so the caller can mutate without poisoning fixtures. */
  return structuredClone(value)
}

/**
 * Echo helper for mock writes. Returns the payload + a synthetic
 * id, after the same latency + error simulation.
 */
export async function mockEcho<T extends object>(
  payload: T,
  { id, signal, delayMs, path, method = 'POST' }: MockRequestOptions & { id?: string | number } = {}
): Promise<T & { id: string | number }> {
  await mockRequest(null, { signal, delayMs, path, method })
  return { id: id ?? cryptoId(), ...structuredClone(payload) }
}

/**
 * Wrap a thunk so identical concurrent reads share one inflight
 * promise. Useful when several components mount simultaneously
 * and each kicks off the same store fetch.
 *
 *   const list = await dedupe(`GET /trademarks?${qs}`, () =>
 *     mockRequest(rows, { signal })
 *   )
 *
 * The promise is removed from the map once it settles (resolve
 * OR reject) so subsequent requests start fresh.
 */
const inflight = new Map<string, Promise<any>>()
export async function dedupe<T>(key: string, factory: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key)
  if (existing) return existing
  const promise = factory()
  inflight.set(key, promise)
  try {
    return await promise
  } finally {
    inflight.delete(key)
  }
}

/* ════════════════════════════════════════════════════════════════
 * Runtime configuration — devtools surface
 * ════════════════════════════════════════════════════════════════ */

/** Switch the latency preset. */
export function setMockTiming(preset: string): void {
  if (preset in TIMING_PRESETS) state.timing = preset as TimingPreset
}

/** Set the random-error probability (0..1). Default 0 = never. */
export function setMockErrorRate(rate: number | string): void {
  state.errorRate = Math.max(0, Math.min(1, Number(rate) || 0))
}

/**
 * Force the next request to throw a specific error. Useful for
 * one-shot QA of error/not-found UI without raising the global
 * error rate.
 *
 *   forceNextError('404')    // or '500' / '401' / 'timeout' / etc.
 */
export function forceNextError(key: string): void {
  if (key in ERROR_FACTORIES) state.pinnedError = key as SimulatedErrorKey
}

/** Add an endpoint path to the random-error allowlist. */
export function allowErrorless(path: string): void {
  state.errorAllowlist.add(path)
}

/** Reset to factory defaults — handy for cypress/vitest teardown. */
export function resetMockSimulator() {
  state.timing = (config.mockTiming as TimingPreset) || 'normal'
  state.errorRate = typeof config.mockErrorRate === 'number' ? config.mockErrorRate : 0
  state.pinnedError = null
  state.errorAllowlist.clear()
  inflight.clear()
}

/* Expose a small devtools handle on `window.__mock` for console
 * tweaking during development. Production builds also include it
 * — harmless when USE_MOCKS is false (no mock path runs). */
if (typeof window !== 'undefined') {
  // A console handle, not an API: declared on globalThis in env.d.ts.
  ;(window as any).__mock = {
    setTiming: setMockTiming,
    setErrorRate: setMockErrorRate,
    forceNext: forceNextError,
    reset: resetMockSimulator,
    get state() {
      return { ...state, errorAllowlist: [...state.errorAllowlist] }
    },
  }
}

/* ════════════════════════════════════════════════════════════════
 * Internals
 * ════════════════════════════════════════════════════════════════ */

function randomLatency(): number {
  const p = TIMING_PRESETS[state.timing] || TIMING_PRESETS.normal
  return Math.floor(p.min + Math.random() * (p.max - p.min))
}

function maybeFailRandomly(path: string): void {
  if (!state.errorRate) return
  if (state.errorAllowlist.has(path)) return
  if (Math.random() >= state.errorRate) return

  /* Pick a random failure weighted by ERROR_WEIGHTS. */
  const r = Math.random()
  let acc = 0
  for (const [key, weight] of ERROR_WEIGHTS) {
    acc += weight
    if (r <= acc) throw makeError(key)
  }
  throw makeError('500')
}

function makeError(key: SimulatedErrorKey): Error {
  const factory = ERROR_FACTORIES[key] || ERROR_FACTORIES['500']
  return factory()
}

/**
 * AbortController-aware sleep. Rejects with an AbortError as soon
 * as the signal fires, matching the spec'd behaviour of fetch().
 */
async function abortableSleep(ms: number, signal?: AbortSignal | null): Promise<void> {
  if (!signal) return sleep(ms)
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      reject(createAbortError(signal.reason))
    }
    if (signal.aborted) onAbort()
    else signal.addEventListener('abort', onAbort, { once: true })
  })
}

function createAbortError(reason?: unknown): Error {
  /* Native DOMException so it duck-types as a real fetch AbortError —
   * `err.name === 'AbortError'` works in stores. */
  const message = typeof reason === 'string' && reason ? reason : 'Aborted'
  if (typeof DOMException !== 'undefined') {
    return new DOMException(message, 'AbortError')
  }
  const e = new Error(message)
  e.name = 'AbortError'
  return e
}

function cryptoId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `id_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  )
}

/**
 * Test helper — predicate used by store actions to swallow abort
 * errors quietly (they're not failures, they're cancellations).
 */
export function isAbortError(err) {
  return err?.name === 'AbortError'
}
